import React from 'react';
import { Alert, Chip, Stack, Typography } from '@mui/material';

import type { UserType } from '@shared/auth/index.js';
import type { CustomStep } from '@shared/patient-intake-status';

import { useAuth } from '../../../../../../hooks/index.js';
import { getUserTypeValidity, USER_TYPES } from '../../../../../../utils/index.js';
import { SingleDocumentListItem } from '../../../../../../components/DocumentManager';

const USERS_WITH_ACCESS_TO_DOCUMENT_UPLOAD: UserType[] = [USER_TYPES.NURSE, USER_TYPES.DOCTOR, USER_TYPES.DIRECTOR];

const throwNever = (value: never): never => {
  throw new Error(`Unhandled value: ${value}`);
};

type DocumentType = CustomStep['DOCUMENT_UPLOAD']['metadata']['documentType'];

const getDocumentTypeDisplayName = (documentType: DocumentType): string => {
  switch (documentType) {
    case 'IDENTIFICATION':
      return 'Identification Document';
    case 'MEDICAL_RECORDS':
      return 'Medical Records';
    case 'PRESCRIPTION':
      return 'Prescription';
    case 'OTHER':
      return 'Other Document';
    default:
      return throwNever(documentType);
  }
};

type FileViewerProps = {
  fileUploadDocId: string;
  patientId: string;
};

const FileViewer = ({ fileUploadDocId, patientId }: FileViewerProps) => {
  const { userType } = useAuth();

  if (!fileUploadDocId) {
    return <Alert severity="info">No document has been uploaded for this step yet.</Alert>;
  }

  if (getUserTypeValidity({ userType, validUsers: USERS_WITH_ACCESS_TO_DOCUMENT_UPLOAD })) {
    return <SingleDocumentListItem documentId={fileUploadDocId} patientId={patientId} type="PATIENT_DOCUMENT_UPLOAD" />;
  }

  return <Alert severity="info">The patient has uploaded a document.</Alert>;
};

type DocumentUploadStepViewerProps = {
  step: CustomStep['DOCUMENT_UPLOAD'];
  patientId: string;
};

export const DocumentUploadStepViewer: React.FC<DocumentUploadStepViewerProps> = ({ step, patientId }) => {
  const { currentStatus } = step;

  if (!currentStatus || currentStatus.status === 'NOT_STARTED' || currentStatus.status === 'ERROR') {
    return null;
  }

  const { metadata } = currentStatus;

  if (!metadata) {
    return null;
  }

  const fileUploadDocId = metadata.fileUploadDocId;

  return (
    <Stack gap={2}>
      {metadata && 'documentType' in metadata && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="subtitle2">Document Type</Typography>
          <Chip label={getDocumentTypeDisplayName(step.metadata.documentType)} size="small" color="primary" />
        </Stack>
      )}

      <FileViewer fileUploadDocId={fileUploadDocId} patientId={patientId} />
    </Stack>
  );
};
